import axiosRaw from 'axios';
import axios from '../axiosInstance';
import { Category, Customer, Exporter, ExporterFilters, ConnectionTestResult } from '../../containers/pages/CANsense/types/exporter';
import { getApiBaseUrl, getForwardedHost } from './apiClient';

/**
 * CANsense Exporter API Client
 * Handles all communication with the CANsense exporter backend
 */

/**
 * Helper function to determine API URL based on environment
 */
const getApiUrl = (endpoint: string): string => {
  return `${getApiBaseUrl()}/sense-exporters${endpoint}`;
};

/**
 * Exporter Service class - handles all exporter-related API calls
 */
export class ExporterService {
  /**
   * Get all categories
   * Route: GET /api3.2/sense-exporters/categories
   */
  async getCategories(): Promise<Category[]> {
    const API_URL = getApiUrl('/categories');
    const response = await axios.get(API_URL, {
      headers: {
        "X-Forwarded-Host": getForwardedHost(API_URL),
      },
    });
    return response.data?.data?.categories || response.data?.categories || [];
  }

  /**
   * Get all customers with exporter counts
   * Route: GET /api3.2/sense-exporters/customers
   */
  async getCustomers(): Promise<Customer[]> {
    const API_URL = getApiUrl('/customers');
    const response = await axios.get(API_URL, {
      headers: {
        "X-Forwarded-Host": getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data || [];
  }

  /**
   * Get all exporters with optional filtering
   * Route: GET /api3.2/sense-exporters/exporters
   */
  async getExporters(filters?: ExporterFilters): Promise<Exporter[]> {
    const API_URL = getApiUrl('/exporters');
    const response = await axios.get(API_URL, {
      params: filters,
      headers: {
        "X-Forwarded-Host": getForwardedHost(API_URL),
      },
    });
    return response.data?.data?.exporters || response.data?.exporters || [];
  }

  /**
   * Get a specific exporter by ID
   * Route: GET /api3.2/sense-exporters/exporters/:id
   */
  async getExporter(id: number): Promise<Exporter> {
    const API_URL = getApiUrl(`/exporters/${id}`);
    const response = await axios.get(API_URL, {
      headers: {
        "X-Forwarded-Host": getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data;
  }

  /**
   * Create a new exporter
   * Route: POST /api3.2/sense-exporters/exporters
   */
  async createExporter(data: Partial<Exporter>): Promise<Exporter> {
    const API_URL = getApiUrl('/exporters');

    try {
      const response = await axios.post(API_URL, data, {
        headers: {
          "X-Forwarded-Host": getForwardedHost(API_URL),
        },
      });
      return response.data?.data || response.data;
    } catch (error) {
      if (axiosRaw.isAxiosError(error) && error.response?.data) {
        const msg = error.response.data?.error?.message || error.response.data?.error || 'Failed to create exporter';
        const err = new Error(msg);
        (err as any).status = error.response.status;
        throw err;
      }
      throw error;
    }
  }

  /**
   * Update an existing exporter
   * Route: PUT /api3.2/sense-exporters/exporters/:id
   */
  async updateExporter(id: number, data: Partial<Exporter>): Promise<Exporter> {
    const API_URL = getApiUrl(`/exporters/${id}`);

    try {
      const response = await axios.put(API_URL, data, {
        headers: {
          "X-Forwarded-Host": getForwardedHost(API_URL),
        },
      });
      return response.data?.data || response.data;
    } catch (error) {
      if (axiosRaw.isAxiosError(error) && error.response?.data) {
        const msg = error.response.data?.error?.message || error.response.data?.error || 'Failed to update exporter';
        const err = new Error(msg);
        (err as any).status = error.response.status;
        throw err;
      }
      throw error;
    }
  }

  /**
   * Delete an exporter
   * Route: DELETE /api3.2/sense-exporters/exporters/:id
   */
  async deleteExporter(id: number): Promise<void> {
    const API_URL = getApiUrl(`/exporters/${id}`);

    try {
      await axios.delete(API_URL, {
        headers: {
          "X-Forwarded-Host": getForwardedHost(API_URL),
        },
      });
    } catch (error) {
      if (axiosRaw.isAxiosError(error) && error.response?.data) {
        const msg = error.response.data?.error?.message || error.response.data?.error || 'Failed to delete exporter';
        const err = new Error(msg);
        (err as any).status = error.response.status;
        throw err;
      }
      throw error;
    }
  }

  /**
   * Sync exporters from Prometheus
   * Route: POST /api3.2/sense-exporters/sync
   */
  async syncFromPrometheus(): Promise<{ synced: number; message: string }> {
    const API_URL = getApiUrl('/sync');
    const response = await axios.post(API_URL, {}, {
      headers: {
        "X-Forwarded-Host": getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data;
  }

  /**
   * Test connection to an exporter endpoint
   * Route: POST /api3.2/sense-exporters/exporters/test
   *
   * Accepts either positional arguments (legacy) or an options object (preferred)
   */
  async testConnection(
    hostOrOptions: string | {
      host: string;
      port: number;
      authType?: 'none' | 'basic' | 'bearer';
      authUsername?: string;
      authPassword?: string;
      authToken?: string;
    },
    port?: number,
    username?: string,
    password?: string
  ): Promise<ConnectionTestResult> {
    const API_URL = getApiUrl('/exporters/test');

    // Normalize input to extract host, port, and credentials
    let targetHost: string;
    let targetPort: number;
    let authUsername: string | undefined;
    let authPassword: string | undefined;

    if (typeof hostOrOptions === 'object') {
      // Object form (preferred)
      targetHost = hostOrOptions.host;
      targetPort = hostOrOptions.port;
      if (hostOrOptions.authType === 'basic') {
        authUsername = hostOrOptions.authUsername;
        authPassword = hostOrOptions.authPassword;
      }
      // Note: Bearer token auth would need backend support
    } else {
      // Legacy positional form
      targetHost = hostOrOptions;
      targetPort = port!;
      authUsername = username;
      authPassword = password;
    }

    try {
      const payload: { target_host: string; port: number; username?: string; password?: string } = {
        target_host: targetHost,
        port: targetPort
      };

      // Add credentials if provided
      if (authUsername && authPassword) {
        payload.username = authUsername;
        payload.password = authPassword;
      }

      const response = await axios.post(API_URL, payload, {
        headers: {
          "X-Forwarded-Host": getForwardedHost(API_URL),
        },
        timeout: 15000,
      });
      return response.data?.data || response.data;
    } catch (error) {
      if (axiosRaw.isAxiosError(error) && error.response?.data) {
        return error.response.data as ConnectionTestResult;
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Connection test failed',
        suggestions: [
          'Verify the IP address is correct',
          'Check if the exporter is running',
          'Ensure the port is open in the firewall',
        ],
      };
    }
  }
}

// Export singleton instance
export const exporterService = new ExporterService();

// Export with original name for backward compatibility
export const exporterApi = exporterService;
